import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { publicApi, guardarSesion } from "@/lib/api";
import { rememberDeviceLogin, getDeviceLoginOffline, isNetworkError, forgetDevice } from "@/lib/localCache";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Delete, LogIn, Loader2 } from "lucide-react";

export default function PosPinLogin() {
  const nav = useNavigate();
  const [pin, setPin] = useState("");
  const [loading, setLoading] = useState(false);
  const deviceToken = localStorage.getItem("aipos_device_token");
  const companyName = localStorage.getItem("aipos_device_company") || "";
  const storeName = localStorage.getItem("aipos_device_store") || "";
  const terminalName = localStorage.getItem("aipos_device_name") || "";

  // Este equipo puede estar emparejado como kiosco, pantalla de cocina, PDA o consulta
  // de precios: en ese caso no se pide PIN de cajero, se abre su propia pantalla.
  useEffect(() => {
    const tipo = localStorage.getItem("aipos_device_tipo");
    const slug = localStorage.getItem("aipos_device_slug");
    if (!deviceToken || !tipo || tipo === "POS" || tipo === "PREVENTA") return;
    const destino = {
      AUTOSERVICIO: `/k/${slug || deviceToken}`,
      CONSULTA_PRECIO: `/consulta/${slug || deviceToken}`,
      KDS: "/kds",
      PDA: "/pda",
    }[tipo];
    if (destino) nav(destino, { replace: true });
  }, [deviceToken, nav]);

  // Confirma con el servidor que el equipo sigue emparejado y a qué terminal/sucursal pertenece hoy
  useEffect(() => {
    if (!deviceToken) return;
    publicApi.get(`/public/device/${deviceToken}`).then(({ data }) => {
      localStorage.setItem("pos_terminal_id", data.terminal_id);
      localStorage.setItem("aipos_device_name", data.nombre || "");
      localStorage.setItem("aipos_device_store", data.store?.nombre || "");
    }).catch(async (e) => {
      if (!e?.response) return;  // sin red: sigue con lo guardado
      await forgetDevice();
      toast.error("Este equipo fue desenlazado. Emparéjalo de nuevo con un código.");
      nav("/pair", { replace: true });
    });
  }, [deviceToken, nav]);

  const press = (d) => setPin((p) => (p.length < 6 ? p + d : p));
  const back = () => setPin((p) => p.slice(0, -1));

  // Deja la sesión en localStorage con el shape que espera useAuth y entra al POS
  const applySession = (s, offline) => {
    guardarSesion(s);
    localStorage.setItem("aipos_user", JSON.stringify(s.user));
    localStorage.setItem("aipos_company", JSON.stringify(s.company));
    localStorage.setItem("aipos_store", JSON.stringify(s.store));
    if (s.terminal_id) localStorage.setItem("pos_terminal_id", s.terminal_id);
    toast.success(offline ? `Bienvenido ${s.user?.name} · modo offline` : `Bienvenido ${s.user?.name}`);
    window.location.href = "/pos";
  };

  const submit = async () => {
    if (pin.length < 4 || !deviceToken) return;
    setLoading(true);
    try {
      const r = await publicApi.post("/pos-pin/login", { device_token: deviceToken, pin });
      await rememberDeviceLogin(pin, r.data);  // permite entrar luego sin Internet
      applySession(r.data, false);
    } catch (e) {
      // Sin conexión: entramos con la última sesión guardada para este PIN en el equipo
      if (isNetworkError(e)) {
        const cached = await getDeviceLoginOffline(pin);
        if (cached) { applySession(cached, true); return; }
        toast.error("Sin conexión: este PIN no está guardado en este equipo. Conéctate una vez para habilitarlo offline.");
      } else {
        if (e?.response?.status === 401 && /emparejado/i.test(e.response.data?.detail || "")) {
          await forgetDevice();
          toast.error("Este equipo fue desenlazado. Emparéjalo de nuevo con un código.");
          nav("/pair", { replace: true });
          return;
        }
        toast.error(e?.response?.data?.detail || "PIN incorrecto");
      }
      setPin("");
    } finally { setLoading(false); }
  };

  if (!deviceToken) {
    return (
      <div className="min-h-screen bg-slate-900 text-white grid place-items-center p-6 text-center">
        <div>
          <p className="text-2xl font-bold">Este equipo no está emparejado</p>
          <Button className="mt-6 h-12 rounded-2xl bg-sky-500 hover:bg-sky-600" onClick={() => nav("/pair")}>
            Emparejar ahora
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-sky-900 text-white grid place-items-center p-6">
      <div className="w-full max-w-sm">
        <div className="text-center mb-6">
          <img src="/brand/aipos-logo.png" alt="Ai-POS" className="w-14 h-14 mx-auto mb-3" />
          <p className="text-xs font-semibold uppercase tracking-widest text-white/60">{companyName}</p>
          <p className="text-sm font-semibold text-white/80">{storeName} · {terminalName}</p>
          <h1 className="text-2xl font-bold font-display mt-3">Ingresa tu PIN</h1>
        </div>

        {/* Dots */}
        <div className="flex justify-center gap-3 my-6" data-testid="pos-pin-dots">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <div key={i} className={`w-4 h-4 rounded-full transition-all ${i < pin.length ? "bg-sky-400 scale-110 shadow-lg shadow-sky-500/50" : "bg-white/15 border border-white/25"}`} />
          ))}
        </div>

        {/* Keypad */}
        <div data-testid="pos-pin-keypad" className="grid grid-cols-3 gap-3">
          {["1", "2", "3", "4", "5", "6", "7", "8", "9"].map((d) => (
            <button
              key={d}
              data-testid={`pos-pin-key-${d}`}
              onClick={() => press(d)}
              className="h-16 rounded-2xl bg-white/10 backdrop-blur-md border border-white/15 text-white text-2xl font-bold hover:bg-white/20 active:scale-95 transition-all"
            >
              {d}
            </button>
          ))}
          <button data-testid="pos-pin-clear" onClick={() => setPin("")} className="h-16 rounded-2xl text-slate-300 text-xs font-semibold hover:bg-white/10">CLR</button>
          <button data-testid="pos-pin-key-0" onClick={() => press("0")} className="h-16 rounded-2xl bg-white/10 backdrop-blur-md border border-white/15 text-white text-2xl font-bold hover:bg-white/20 active:scale-95">0</button>
          <button data-testid="pos-pin-back" onClick={back} className="h-16 rounded-2xl text-slate-300 grid place-items-center hover:bg-white/10">
            <Delete className="w-6 h-6" />
          </button>
        </div>

        <Button
          data-testid="pos-pin-submit"
          onClick={submit}
          disabled={pin.length < 4 || loading}
          className="w-full mt-5 h-14 rounded-2xl bg-emerald-500 hover:bg-emerald-600 text-white text-base font-bold disabled:opacity-30"
        >
          {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Verificando...</> : <><LogIn className="w-4 h-4 mr-2" /> Entrar al POS</>}
        </Button>

        <p className="text-center text-xs text-white/50 mt-4">
          ¿Cambiar de equipo? <button onClick={async () => { await forgetDevice(); nav("/pair"); }} className="text-sky-300 underline">Emparejar de nuevo</button>
        </p>
      </div>
    </div>
  );
}
